import React, { Component, ReactNode, ErrorInfo } from 'react'

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: React.FC<{ error: Error; resetErrorBoundary: () => void }>
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Changelog Error Boundary caught an error:', error, errorInfo)
  }

  resetErrorBoundary = () => {
    this.setState({ hasError: false, error: null })
  }

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback
        return (
          <FallbackComponent
            error={this.state.error}
            resetErrorBoundary={this.resetErrorBoundary}
          />
        )
      }

      return (
        <div
          className="changelog-error-boundary p-6 text-center border rounded-lg"
          style={{
            backgroundColor: 'var(--changelog-fix-bg)',
            borderColor: 'var(--changelog-fix-border)',
            color: 'var(--changelog-fix-text)',
            boxShadow: 'var(--changelog-shadow)',
          }}
        >
          <h3
            className="text-lg font-semibold mb-2"
            style={{ color: 'var(--changelog-tertiary-text)' }}
          >
            Something went wrong
          </h3>
          <p className="mb-4" style={{ color: 'var(--changelog-text)' }}>
            {this.state.error.message}
          </p>
          <button
            onClick={this.resetErrorBoundary}
            className="px-4 py-2 rounded focus:outline-none focus:ring-2"
            style={{
              backgroundColor: 'var(--changelog-secondary)',
              color: 'var(--changelog-secondary-text)',
              border: 'none',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.8'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1'
            }}
            onFocus={(e) => {
              e.currentTarget.style.boxShadow = `0 0 0 2px var(--changelog-input-focus)`
            }}
            onBlur={(e) => {
              e.currentTarget.style.boxShadow = 'none'
            }}
          >
            Try again
          </button>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
